import { useCallback, useContext, useEffect, useMemo } from "react";
import { Theme, useMediaQuery } from "@securitize/reactjs-whm";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils"; // TODO: We only use the Utils, find a way to import only that.
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SID } from "@securitize/domain";
import { FormType, AuthRegistrationDataDto } from "./types";
import MobileForm from "./MobileForm";
import DesktopForm from "./DesktopForm";
import { I18nContext } from "../../contexts/I18nContext";
import translationKeys from "../../contexts/I18nContext/translationKeys";
import config from "../../data/config";
import { getDialCodeByCountryCode } from "../../helpers/phoneHelper";
import {
  REGISTRATION_STEP_PATHS,
  RegistrationSteps,
} from "./routing-constants";
import { CountriesProvider } from "../shared/CountryTextField/CountriesContext";
import {
  allowedEntityNameCharsRegex,
  allowedIndividualNameCharsRegex,
} from "../../utils/validations";
import { PrefillForm } from "./PrefillForm";
import { INVESTOR_TYPES, STEP_FIELDS } from "./constants";
import { MFEContext } from "../../contexts/MFEContext";
import { MIXPANEL_EVENTS } from "../../helpers/mixpanel";

const useStepValidation = () => {
  const { getTranslation } = useContext(I18nContext);

  return useMemo<Record<RegistrationSteps, Yup.Schema>>(
    () => ({
      [REGISTRATION_STEP_PATHS.name]: Yup.object().shape({
        firstName: Yup.string()
          .required(
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_REQUIRED_FIRST_NAME,
            ),
          )
          .matches(
            allowedIndividualNameCharsRegex,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_INVALID_NAME,
            ),
          )
          .min(
            2,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_MINIMUM_LENGTH,
            ),
          )
          .max(
            32,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_MAXIMUM_LENGTH,
            ),
          ),
        lastName: Yup.string()
          .required(
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_REQUIRED_LAST_NAME,
            ),
          )
          .matches(
            allowedIndividualNameCharsRegex,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_INVALID_NAME,
            ),
          )
          .min(
            2,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_MINIMUM_LENGTH,
            ),
          )
          .max(
            32,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_MAXIMUM_LENGTH,
            ),
          ),
      }),
      [REGISTRATION_STEP_PATHS.phone]: Yup.object().shape({
        country: Yup.object().required(
          getTranslation(
            translationKeys.REGISTRATION_VALIDATION_REQUIRED_RESIDENCE_COUNTRY,
          ),
        ),
        state: Yup.object().when("country", {
          is: (val: { value: string } | null) => val?.value === "US",
          then: (schema) =>
            schema.required(
              getTranslation(
                translationKeys.REGISTRATION_VALIDATION_REQUIRED_RESIDENCE_STATE,
              ),
            ),
          otherwise: (schema) => schema.nullable(),
        }),
        phone: Yup.string()
          .matches(
            /^\d+$/,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_INVALID_PHONE,
            ),
          )
          .required(
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_REQUIRED_PHONE,
            ),
          )
          .test(
            "isValidPhone",
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_INVALID_PHONE,
            ),
            function (value) {
              return intlTelInput.utils!.isValidNumber(
                value,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                this.parent.country?.value as string,
              );
            },
          ),
      }),
      [REGISTRATION_STEP_PATHS.accountType]: Yup.object().shape({
        investorType: Yup.string().required(
          getTranslation(
            translationKeys.REGISTRATION_VALIDATION_REQUIRED_ACCOUNT_TYPE,
          ),
        ),
      }),
      [REGISTRATION_STEP_PATHS.entityInfo]: Yup.object().shape({
        entityName: Yup.string()
          .required(
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_REQUIRED_ENTITY_NAME,
            ),
          )
          .matches(
            allowedEntityNameCharsRegex,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_INVALID_ENTITY_NAME,
            ),
          )
          .min(
            2,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_MINIMUM_LENGTH,
            ),
          )
          .max(
            32,
            getTranslation(
              translationKeys.REGISTRATION_VALIDATION_MAXIMUM_LENGTH,
            ),
          ),
        entityCountry: Yup.object().required(
          getTranslation(
            translationKeys.REGISTRATION_VALIDATION_REQUIRED_ENTITY_COUNTRY,
          ),
        ),
        entityState: Yup.object().when("entityCountry", {
          is: (val: { value: string } | null) => val?.value === "US",
          then: (schema) =>
            schema.required(
              getTranslation(
                translationKeys.REGISTRATION_VALIDATION_REQUIRED_ENTITY_STATE,
              ),
            ),
          otherwise: (schema) => schema.nullable(),
        }),
      }),
    }),
    [getTranslation],
  );
};

function RegistrationContent() {
  const { language } = useContext(I18nContext);
  const { mixPanel } = useContext(MFEContext);
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const validationSchema = useStepValidation();
  const [queryParams] = useSearchParams();
  const entityAuthorizationCode = queryParams.get("code") ?? undefined;
  const issuerId =
    queryParams.get("issuerId") ?? queryParams.get("issuer_id") ?? undefined;
  const redirectUrl = queryParams.get("redirectUrl") ?? undefined;
  const email = queryParams.get("email") ?? undefined;
  const brokerDealerNotAcceptedParam = queryParams.get(
    "brokerDealerNotAccepted",
  );
  const brokerDealerNotAccepted =
    brokerDealerNotAcceptedParam !== null
      ? brokerDealerNotAcceptedParam === "true"
      : undefined;
  const { step } = useParams<{ step: RegistrationSteps }>();
  const navigate = useNavigate();

  const isByReference = !!(entityAuthorizationCode || issuerId || redirectUrl);

  useEffect(() => {
    mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.VISIT);
    if (isByReference) {
      mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.INIT, {
        RedirectedFrom: "validation-visit",
      });
      mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.TRY, {
        RedirectedFrom: "validation-visit",
      });
    }
  }, [mixPanel, isByReference, queryParams]);

  const navigateAndKeepQueryParams = useCallback(
    (newStep: RegistrationSteps) => {
      const search = queryParams.toString();
      navigate(search ? `${newStep}?${search}` : newStep);
    },
    [navigate, queryParams],
  );

  useEffect(() => {
    // When registration component is mounted, navigate to the first step
    navigateAndKeepQueryParams(REGISTRATION_STEP_PATHS.name);
  }, [navigateAndKeepQueryParams]);

  const goToStep = (
    newStep: RegistrationSteps,
    formik: FormikHelpers<FormType>,
  ) => {
    for (const field of STEP_FIELDS[newStep]) {
      void formik.setFieldTouched(field, false);
    }

    navigateAndKeepQueryParams(newStep);
  };

  return (
    <Formik<FormType>
      initialValues={{
        firstName: "",
        lastName: "",
        country: null,
        state: null,
        phone: "",
        entityName: "",
        entityCountry: null,
        entityState: null,
        investorType: undefined,
      }}
      validationSchema={validationSchema[step!]}
      onSubmit={(values, formik) => {
        mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.TRY);
        if (step === REGISTRATION_STEP_PATHS.name) {
          mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.INIT);
          goToStep(REGISTRATION_STEP_PATHS.phone, formik);
          return;
        } else if (step === REGISTRATION_STEP_PATHS.phone) {
          goToStep(REGISTRATION_STEP_PATHS.accountType, formik);
          return;
        } else if (
          step === REGISTRATION_STEP_PATHS.accountType &&
          values.investorType === INVESTOR_TYPES.ENTITY
        ) {
          goToStep(REGISTRATION_STEP_PATHS.entityInfo, formik);
          return;
        }

        const dialCode = `+${
          getDialCodeByCountryCode(values.country!.value) || ""
        }`;

        const data: AuthRegistrationDataDto = {
          firstName: values.firstName,
          lastName: values.lastName,
          phone: {
            code: dialCode,
            number: values.phone,
            fullNumber: `${dialCode}${values.phone}`,
          },
          investorData: {
            countryCode: values.country!.value,
            state: values.state?.value || undefined,
            analyticsEnabled: false, // Hardcoded to false
            investorType: values.investorType as SID.InvestorType,
            language: language,
            entityDetails: values.entityName
              ? {
                  entityName: values.entityName.replace(/\s+/g, " ").trim(),
                  countryCode: values.entityCountry!.value,
                  state: values.entityState?.value,
                }
              : undefined,
          },
          entityAuthorizationCode,
          issuerId,
          ...(issuerId && { registrationSource: issuerId }),
          ...(brokerDealerNotAccepted !== undefined && {
            brokerDealerNotAccepted,
          }),
        };

        const encoded = btoa(JSON.stringify(data));

        const urlParams = new URLSearchParams({ reg_data: encoded });
        if (issuerId) {
          urlParams.set("issuerId", issuerId);
        }
        if (redirectUrl) {
          urlParams.set("redirect_uri", redirectUrl);
        }
        if (email) {
          urlParams.set("email_field", email);
        }
        const nextStepUrl = `${config.AUTH_REGISTER_URL}?${urlParams.toString()}`;
        mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.EMAIL_PASSWORD);
        mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.COMPLETE);
        window.location.assign(nextStepUrl);
      }}
    >
      <Form>
        <PrefillForm />
        {mobile ? <MobileForm step={step!} /> : <DesktopForm step={step!} />}
      </Form>
    </Formik>
  );
}

export default function Registration() {
  return (
    <CountriesProvider>
      <RegistrationContent />
    </CountriesProvider>
  );
}
